/**
 * Add Payment Method Modal
 * Uses Stripe Setup Intent to add cards without charging
 */

import { useState, useEffect } from 'react'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useAuth } from '../auth/AuthContext'
import { createSetupIntent, savePaymentMethod } from '../../api/paymentMethods'

interface AddPaymentMethodModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function AddPaymentMethodModal({
  isOpen,
  onClose,
  onSuccess
}: AddPaymentMethodModalProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { user } = useAuth()

  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [setAsDefault, setSetAsDefault] = useState(true)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null)

  // Create Setup Intent when modal opens
  useEffect(() => {
    if (isOpen && !clientSecret) {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      createSetupIntent(token)
        .then((response) => {
          setClientSecret(response.client_secret)
          setSetupIntentId(response.setup_intent_id)
        })
        .catch((err) => {
          setError(err.message || 'Failed to initialize payment form')
        })
    }
  }, [isOpen, clientSecret])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setClientSecret(null)
      setSetupIntentId(null)
      setError(null)
      setIsProcessing(false)
      setSetAsDefault(true)
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const token = localStorage.getItem('auth_token')
    if (!stripe || !elements || !clientSecret || !token || !setupIntentId) {
      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      // Confirm the Setup Intent with the card details
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        throw new Error('Card element not found')
      }

      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: user?.email
            }
          }
        }
      )

      if (confirmError) {
        throw new Error(confirmError.message || 'Failed to verify card')
      }

      if (setupIntent.status !== 'succeeded') {
        throw new Error('Card verification failed')
      }

      // Save payment method to database
      await savePaymentMethod(token, {
        setup_intent_id: setupIntentId,
        set_as_default: setAsDefault
      })

      // Success!
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to add card')
    } finally {
      setIsProcessing(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Add New Card</h2>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Card Element */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Information
            </label>
            <div className="p-3 border border-gray-300 rounded-lg">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#1f2937',
                      '::placeholder': {
                        color: '#9ca3af'
                      }
                    },
                    invalid: {
                      color: '#ef4444'
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Set as default checkbox */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={setAsDefault}
              onChange={(e) => setSetAsDefault(e.target.checked)}
              className="w-4 h-4 text-pink-500 focus:ring-pink-500 rounded"
            />
            <span className="text-sm text-gray-700">
              Set as default payment method
            </span>
          </label>

          {/* Info text */}
          <p className="text-xs text-gray-500">
            Your card will be securely saved for future purchases. No charge will be made at this time.
          </p>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={!stripe || isProcessing || !clientSecret}
              className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              style={{background: 'linear-gradient(135deg, #ffc9e3 0%, #ffb3d9 100%)'}}
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </span>
              ) : (
                'Add Card'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
